import { apiPost } from "../../Api";
import apiGet from "../../Api";
import { mediaPath, MEDIA_VARIANTS } from '../../media';

export const GAME_CONTENT_CACHE_VERSION = 'loot-v3-recipes';

const pendingCraftOperationIds = new Map();
const ITEM_INSTANCE_CACHE_LIMIT = 200;
const itemInstanceCache = new Map();

function cacheItemInstance(itemInstanceId, itemInstance) {
    if (!itemInstanceId || !itemInstance) return;

    itemInstanceCache.delete(itemInstanceId);
    itemInstanceCache.set(itemInstanceId, itemInstance);
    if (itemInstanceCache.size > ITEM_INSTANCE_CACHE_LIMIT) {
        itemInstanceCache.delete(itemInstanceCache.keys().next().value);
    }
}

export function createGameOperationId() {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
        const random = Math.floor(Math.random() * 16);
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

async function readCraftResult(response) {
    if (response?.ok !== true) return '';
    try {
        const readableResponse = typeof response.clone === 'function'
            ? response.clone()
            : response;
        if (typeof readableResponse?.text !== 'function') return '';
        const bodyText = (await readableResponse.text()).trim();
        if (!bodyText) return '';
        try {
            return JSON.parse(bodyText);
        } catch {
            return bodyText;
        }
    } catch (error) {
        console.error('Failed to verify the crafting response', error);
        return '';
    }
}

function contentCacheKey(type, id) {
    return `${GAME_CONTENT_CACHE_VERSION}_${type}_${id}`;
}

//for js
export default class GameAPI {



    static buildQuery(params = {}) {
        const entries = Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== "");
        if (!entries.length) {
            return "";
        }

        const search = new URLSearchParams();
        entries.forEach(([key, value]) => {
            search.append(key, value);
        });

        const query = search.toString();
        return query ? `?${query}` : "";
    }



    static async fetchUser() {
        try {
            const response = await apiGet(`/Client/user`);
            // const user = await response.json();
            const user = JSON.parse(await response.text());
            return user;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static async fetchBootstrap() {
        const response = await apiGet('/client/bootstrap');
        return response.json();
    }

    static async fetchBaseCamp() {
        const response = await apiGet('/BaseCamp');
        return response.json();
    }

    static async enterBaseCamp({ operationId } = {}) {
        if (!operationId) {
            throw new Error('A Camp travel operation ID is required.');
        }
        const response = await apiPost(
            '/BaseCamp/enter',
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async exitBaseCamp({ operationId } = {}) {
        if (!operationId) {
            throw new Error('A Camp travel operation ID is required.');
        }
        const response = await apiPost(
            '/BaseCamp/exit',
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async setBaseCampRespawnPoint({ operationId } = {}) {
        if (!operationId) {
            throw new Error('A Camp respawn operation ID is required.');
        }
        const response = await apiPost(
            '/BaseCamp/respawn-point',
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async travelRegionExit(exitKey, { operationId } = {}) {
        if (!exitKey || !operationId) {
            throw new Error('A region exit and operation ID are required.');
        }
        const response = await apiPost(
            `/RegionTravel/exits/${encodeURIComponent(exitKey)}`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async installBaseCampRoute(regionId, { operationId } = {}) {
        if (!regionId || !operationId) {
            throw new Error('A Camp route and operation ID are required.');
        }
        const response = await apiPost(
            `/BaseCamp/routes/${encodeURIComponent(regionId)}/install`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async travelBaseCampRoute(regionId, { operationId } = {}) {
        if (!regionId || !operationId) {
            throw new Error('A Camp route and operation ID are required.');
        }
        const response = await apiPost(
            `/BaseCamp/routes/${encodeURIComponent(regionId)}/travel`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }


    static async fetchItemTypes() {
        try {
            const response = await apiGet(`/itemtype`);
            // const user = await response.json();
            const user = JSON.parse(await response.text());
            return user;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static async fetchItemType(itemTypeId) {
        if (!itemTypeId) return null;
        try {
            const cachedItemType = GameAPI.fetchItemTypeCached(itemTypeId);
            if (cachedItemType) {
                return cachedItemType;
            }

            const response = await apiGet(`/itemtype/${encodeURIComponent(itemTypeId)}`);
            if (!response.ok) return null;
            const itemType = await response.json();
            localStorage.setItem(contentCacheKey('itemType', itemTypeId), JSON.stringify(itemType));

            return itemType;
        } catch (error) {
            console.error('Error fetching item type:', error);
            return null;
        }
    }

    static fetchItemTypeCached(itemTypeId) {
        if (!itemTypeId) return null;
        const cachedItemType = localStorage.getItem(contentCacheKey('itemType', itemTypeId));
        return cachedItemType ? JSON.parse(cachedItemType) : null;
    }

    //GetCraftableItems
    static async fetchCraftableItems() {
        const response = await apiGet('/item/GetCraftableItems');
        return response.json();
    }

    static async fetchCurrentRegion() {
        try {
            const response = await apiGet(`/Client/currentRegion`);
            const region = await response.json();
            return region;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static async fetchEnemyType(enemyTypeId) {
        if (!enemyTypeId) return null;
        try {
            const response = await apiGet(`/enemytype/${encodeURIComponent(enemyTypeId)}`);
            return response.ok ? await response.json() : null;
        } catch (error) {
            console.error('Error fetching enemy type:', error);
            return null;
        }
    }

    static async fetchTileTypes(filters = {}) {
        try {
            const query = GameAPI.buildQuery({ type: 'baseTile', ...filters });
            const response = await apiGet(`/tiletype${query}`);
            const tiles = await response.json();
            return tiles;
        } catch (error) {
            console.error('Error fetching tile types:', error);
            return [];
        }
    }

    static async fetchTileDecorations(filters = {}) {
        try {
            const query = GameAPI.buildQuery({ type: 'detail', ...filters });
            const response = await apiGet(`/tiletype${query}`);
            const decorations = await response.json();
            return decorations;
        } catch (error) {
            console.error('Error fetching tile decorations:', error);
            return [];
        }
    }

    static async fetchRegionMap(regionId) {
        if (!regionId) return null;
        try {
            const response = await apiGet(`/regionmap/${regionId}`);
            if (!response.ok) {
                throw new Error(`Failed to load region map ${response.status}`);
            }
            const payload = await response.json();
            return payload;
        } catch (error) {
            console.error('Error fetching region map:', error);
            return null;
        }
    }

    //GetCurrentLogs
    static async fetchLogs() {
        try {
            var now = new Date();
            var dateTimeString = now.toISOString();
            const response = await apiGet(`/Client/logs/` + dateTimeString);
            const logs = await response.json();
            return logs;
        } catch (error) {
            console.error('Error :', error);
        }
    }


    static async fetchImage(imageId, variant = MEDIA_VARIANTS.PICTURE) {
        try {
            const response = await apiGet(mediaPath(imageId, variant));
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            return objectURL;
            // this.setState({ imagePreviewUrl: objectURL });
        } catch (error) {
            console.error('Error fetching image:', error);
        }
    };

    static async eventStream() {
        const webSocket = new WebSocket('ws://localhost:3000/clientws');

        webSocket.onmessage = function (event) {
            const data = JSON.parse(event.data);
            // Aktualisieren Sie Ihren State hier
            console.log(data);
        };

        return () => {
            webSocket.close();
        };
    }

    //fetch item with itemId also cache the items in the local storage

    static async fetchItem(itemId) {
        try {
            // Check if the item is in local storage
            const cachedItem = localStorage.getItem(contentCacheKey('item', itemId));
            if (cachedItem) {
                return JSON.parse(cachedItem);
            }

            // If not, fetch the item from the API
            const response = await apiGet(`/item/${itemId}`);
            const item = await response.json();

            // Store the item in local storage
            localStorage.setItem(contentCacheKey('item', itemId), JSON.stringify(item));

            return item;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static async fetchItems(itemIds) {




        const itemIdsToLoad = itemIds.filter((itemId) => !GameAPI.fetchItemCached(itemId));

        if (itemIdsToLoad.length === 0) {
            return;
        }
        await Promise.all(itemIdsToLoad.map((itemId) => GameAPI.fetchItem(itemId)));



    }

    //fetch item cached
    static fetchItemCached(itemId) {
        const cachedItem = localStorage.getItem(contentCacheKey('item', itemId));
        if (cachedItem) {
            return JSON.parse(cachedItem);
        }
    }

    static async fetchItemInstance(itemInstanceId) {
        try {
            const response = await apiGet(`/clientitem/${itemInstanceId}`);
            const itemInstance = await response.json();
            cacheItemInstance(itemInstanceId, itemInstance);
            return itemInstance;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static async fetchItemInstances(itemInstanceIds) {




        const itemIdsToLoad = itemInstanceIds.filter((itemInstanceId) => !GameAPI.fetchItemInstanceCached(itemInstanceId));

        if (itemIdsToLoad.length === 0) {
            return;
        }
        await Promise.all(itemIdsToLoad.map((itemInstanceId) => GameAPI.fetchItemInstance(itemInstanceId)));



    }

    //fetch item cached
    static fetchItemInstanceCached(itemInstanceId) {
        if (!itemInstanceCache.has(itemInstanceId)) return null;

        const itemInstance = itemInstanceCache.get(itemInstanceId);
        itemInstanceCache.delete(itemInstanceId);
        itemInstanceCache.set(itemInstanceId, itemInstance);
        return itemInstance;
    }

    static fetchItemAuto(itemId, itemInstanceId) {
        if (itemInstanceId && itemInstanceId !== "") {
            return GameAPI.fetchItemInstance(itemInstanceId);
        } else {
            return GameAPI.fetchItem(itemId);
        }
    }

    static fetchItemAutoCached(itemId, itemInstanceId) {
        if (itemInstanceId && itemInstanceId !== "") {
            return GameAPI.fetchItemInstanceCached(itemInstanceId);
        } else {
            return GameAPI.fetchItemCached(itemId);
        }
    }

    static async fetchItemsAuto(itemAutoIds) {
        const itemIdsToLoad = itemAutoIds.filter((itm) => !GameAPI.fetchItemAutoCached(itm.itemId, itm.itemInstanceId));
        await Promise.all(itemIdsToLoad.map((itm) => GameAPI.fetchItemAuto(itm.itemId, itm.itemInstanceId)));

        const itemTypeIds = [...new Set(itemAutoIds
            .map((itm) => GameAPI.fetchItemAutoCached(itm.itemId, itm.itemInstanceId)?.typeId)
            .filter(Boolean))];
        await Promise.all(itemTypeIds.map((itemTypeId) => GameAPI.fetchItemType(itemTypeId)));
    }

    static async fetchConsumableEffectTargets() {
        try {
            const cacheKey = contentCacheKey('consumableEffectTargets', 'all');
            const cachedTargets = localStorage.getItem(cacheKey);
            if (cachedTargets) {
                return JSON.parse(cachedTargets);
            }

            const response = await apiGet('/item/consumableEffectTargets');
            if (!response.ok) {
                return [];
            }
            const targets = await response.json();
            localStorage.setItem(cacheKey, JSON.stringify(targets));
            return targets;
        } catch (error) {
            console.error('Failed to load consumable effect targets:', error);
            return [];
        }
    }

    static async fetchStat(statId) {
        try {
            // Check if the item is in local storage
            const cachedItem = localStorage.getItem(contentCacheKey('stat', statId));
            if (cachedItem) {
                return JSON.parse(cachedItem);
            }

            // If not, fetch the item from the API
            const response = await apiGet(`/stat/${statId}`);
            const item = await response.json();
            console.log(item);
            // Store the item in local storage
            localStorage.setItem(contentCacheKey('stat', statId), JSON.stringify(item));

            return item;
        } catch (error) {
            console.error('Error :', error);
        }
    }

    static equipItem(itemInstanceId, itemId, x, y) {
        //make form
        const formData = new FormData();
        formData.append('itemInstanceId', itemInstanceId);
        formData.append('itemId', itemId);
        formData.append('x', x);
        formData.append('y', y);
        return apiPost('/game/equip', formData);
    }
    static unEquipItem(itemId, x, y) {
        //make form
        const formData = new FormData();
        formData.append('itemId', itemId);
        formData.append('x', x);
        formData.append('y', y);

        return apiPost('/game/unequip', formData);
    }

    static moveItem(itemInstanceId, itemId, fromX, fromY, toX, toY, quantity = 0) {
        //make form
        const formData = new FormData();
        formData.append('itemInstanceId', itemInstanceId || '');
        formData.append('itemId', itemId || '');
        formData.append('fromX', fromX);
        formData.append('fromY', fromY);
        formData.append('toX', toX);
        formData.append('toY', toY);
        if (quantity > 0) formData.append('quantity', quantity);
        return apiPost('/game/moveItem', formData);
    }

    static async craftItem(itemId, {
        rarityId = '', itemLevel = null, operationId = null,
    } = {}) {
        const operationKey = `${String(itemId || '')}:${String(rarityId || '')}:${String(itemLevel || '')}`;
        const resolvedOperationId = operationId
            || pendingCraftOperationIds.get(operationKey)
            || createGameOperationId();
        pendingCraftOperationIds.set(operationKey, resolvedOperationId);

        //make form
        const formData = new FormData();
        formData.append('itemId', itemId);
        formData.append('operationId', resolvedOperationId);
        if (rarityId) formData.append('rarityId', rarityId);
        if (itemLevel) formData.append('itemLevel', itemLevel);

        const response = await apiPost('/game/craftItem', formData);
        const result = await readCraftResult(response);
        const succeeded = result === 'Item crafted'
            || (result && typeof result === 'object');
        if (
            succeeded
            && pendingCraftOperationIds.get(operationKey)
                === resolvedOperationId
        ) {
            pendingCraftOperationIds.delete(operationKey);
        }
        if (!succeeded) {
            const error = new Error(
                result || 'The item could not be crafted.',
            );
            error.status = response?.status;
            throw error;
        }
        return result && typeof result === 'object' ? result : response;
    }

    static SetDesiredAction(desiredAction, { forceIfRepeated = true } = {}) {
        //make form
        const formData = new FormData();
        formData.append('desiredAction', desiredAction);
        formData.append('forceIfRepeated', String(forceIfRepeated));

        return apiPost('/game/setDesiredAction', formData);
    }

    static SetMovementTarget(x, y, options = {}) {
        const payload = { x, y };
        if (Number.isInteger(options.stopWithinDistance)) {
            payload.stopWithinDistance = options.stopWithinDistance;
        }
        if (typeof options.arrivalAction === 'string' && options.arrivalAction) {
            payload.arrivalAction = options.arrivalAction;
        }
        if (typeof options.scavengeTargetPlacementId === 'string'
            && options.scavengeTargetPlacementId) {
            payload.scavengeTargetPlacementId = options.scavengeTargetPlacementId;
        }
        return apiPost('/game/movementTarget', JSON.stringify(payload));
    }

    static async depositBaseCampBuilding(buildingKey, {
        operationId,
        materials,
    } = {}) {
        if (!buildingKey || !operationId) {
            throw new Error('A Camp building and operation ID are required.');
        }
        const payload = { operationId };
        if (Array.isArray(materials) && materials.length > 0) {
            payload.materials = materials;
        }
        const response = await apiPost(
            `/BaseCamp/buildings/${encodeURIComponent(buildingKey)}/deposit`,
            JSON.stringify(payload),
        );
        return response.json();
    }

    static async startBaseCampBuilding(buildingKey, { operationId } = {}) {
        if (!buildingKey || !operationId) {
            throw new Error('A Camp building and operation ID are required.');
        }
        const response = await apiPost(
            `/BaseCamp/buildings/${encodeURIComponent(buildingKey)}/start`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async cancelBaseCampBuilding(buildingKey, { operationId } = {}) {
        if (!buildingKey || !operationId) {
            throw new Error('A Camp building and operation ID are required.');
        }
        const response = await apiPost(
            `/BaseCamp/buildings/${encodeURIComponent(buildingKey)}/cancel`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async transferBaseCampStorage(transfer = {}) {
        if (!transfer.operationId) {
            throw new Error('A storage transfer operation ID is required.');
        }
        const response = await apiPost(
            '/BaseCamp/storage/transfer',
            JSON.stringify(transfer),
        );
        return response.json();
    }

    static async startChampionEncounter(placementId, { operationId } = {}) {
        if (!placementId || !operationId) {
            throw new Error('A champion placement and operation ID are required.');
        }
        const response = await apiPost(
            `/ChampionEncounters/${encodeURIComponent(placementId)}/start`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async cancelGathering() {
        const response = await apiPost('/Gathering/cancel');
        return response.json();
    }

    static async fetchGatheringNodes() {
        const response = await apiGet('/Gathering/nodes');
        return response.json();
    }

    static async fetchGroundLoot() {
        const response = await apiGet('/ground-loot');
        return response.json();
    }

    static async fetchStarterCache() {
        const response = await apiGet('/starter-cache');
        return response.json();
    }

    static async fetchGroundLootContainer(containerId) {
        if (!containerId) {
            throw new Error('A treasure container is required.');
        }
        const response = await apiGet(`/ground-loot/${encodeURIComponent(containerId)}`);
        return response.json();
    }

    static async fetchWorldLootContainer(containerId) {
        if (!containerId) {
            throw new Error('An authored loot container is required.');
        }
        const response = await apiGet(`/world-loot/${encodeURIComponent(containerId)}`);
        return response.json();
    }

    static async openWorldLootContainer(containerId, { operationId } = {}) {
        if (!containerId) {
            throw new Error('An authored loot container is required.');
        }
        const response = await apiPost(
            `/world-loot/${encodeURIComponent(containerId)}/open`,
            JSON.stringify({ OperationId: operationId }),
        );
        return response.json();
    }

    static async dropGroundLoot({
        operationId,
        sourceX,
        sourceY,
        itemId,
        itemInstanceId,
        quantity,
        targetX,
        targetY,
    }) {
        const response = await apiPost('/ground-loot/drop', JSON.stringify({
            OperationId: operationId,
            SourceX: sourceX,
            SourceY: sourceY,
            ItemId: itemId,
            ItemInstanceId: itemInstanceId || '',
            Quantity: quantity,
            TargetX: targetX,
            TargetY: targetY,
        }));
        return response.json();
    }

    static async takeGroundLoot(containerId, entryId, {
        operationId,
        destinationX,
        destinationY,
    } = {}) {
        if (!containerId || !entryId) {
            throw new Error('A treasure container and entry are required.');
        }
        const payload = {
            OperationId: operationId,
        };
        if (Number.isInteger(destinationX) && Number.isInteger(destinationY)) {
            payload.DestinationX = destinationX;
            payload.DestinationY = destinationY;
        }
        const response = await apiPost(
            `/ground-loot/${encodeURIComponent(containerId)}/entries/${encodeURIComponent(entryId)}/take`,
            JSON.stringify(payload),
        );
        return response.json();
    }

    static async takeWorldLoot(containerId, entryId, {
        operationId,
        destinationX,
        destinationY,
    } = {}) {
        if (!containerId || !entryId) {
            throw new Error('An authored loot container and entry are required.');
        }
        const payload = { OperationId: operationId };
        if (Number.isInteger(destinationX) && Number.isInteger(destinationY)) {
            payload.DestinationX = destinationX;
            payload.DestinationY = destinationY;
        }
        const response = await apiPost(
            `/world-loot/${encodeURIComponent(containerId)}/entries/${encodeURIComponent(entryId)}/take`,
            JSON.stringify(payload),
        );
        return response.json();
    }

    static async depositWorldLoot(containerId, {
        operationId,
        sourceX,
        sourceY,
        itemId,
        itemInstanceId,
        quantity,
        slot,
    } = {}) {
        if (!containerId) {
            throw new Error('An authored loot container is required.');
        }
        const payload = {
            OperationId: operationId,
            SourceX: sourceX,
            SourceY: sourceY,
            ItemId: itemId,
            ItemInstanceId: itemInstanceId || '',
            Quantity: quantity,
        };
        if (Number.isInteger(slot)) payload.Slot = slot;
        const response = await apiPost(
            `/world-loot/${encodeURIComponent(containerId)}/deposit`,
            JSON.stringify(payload),
        );
        return response.json();
    }

    static async takeStarterCache(cacheId, entryId, { operationId } = {}) {
        if (!cacheId || !entryId) {
            throw new Error('A starter cache and entry are required.');
        }
        const response = await apiPost(
            `/starter-cache/${encodeURIComponent(cacheId)}/entries/${encodeURIComponent(entryId)}/take`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async consumeItem({ itemId, x, y, source = 'pack', slotIndex, operationId }) {
        const fromBelt = source === 'belt';
        if (
            !itemId
            || (fromBelt
                ? !Number.isInteger(Number(slotIndex))
                : (!Number.isInteger(Number(x)) || !Number.isInteger(Number(y))))
        ) {
            throw new Error('A consumable source slot is required.');
        }
        const response = await apiPost('/game/items/consume', JSON.stringify({
            itemId,
            ...(fromBelt ? { source } : {}),
            ...(fromBelt
                ? { slotIndex: Number(slotIndex) }
                : { x: Number(x), y: Number(y) }),
            operationId,
        }));
        return response.json();
    }

    static async transferQuickBelt(request) {
        if (!request?.operationId || !request?.itemId || !request?.direction) {
            throw new Error('A belt transfer and operation ID are required.');
        }
        const response = await apiPost(
            '/game/items/belt/transfer',
            JSON.stringify(request),
        );
        return response.json();
    }

    static async learnRecipe({ itemId, x, y, operationId }) {
        if (
            !itemId
            || !operationId
            || !Number.isInteger(Number(x))
            || !Number.isInteger(Number(y))
        ) {
            throw new Error('A recipe item, Pack slot, and operation ID are required.');
        }
        const response = await apiPost('/game/items/learn-recipe', JSON.stringify({
            operationId,
            itemId,
            x: Number(x),
            y: Number(y),
        }));
        return response.json();
    }

    static async salvageItem({
        itemId,
        itemInstanceId,
        x,
        y,
        source = 'pack',
        sourceSlotIndex,
        operationId,
        confirmStarterRemoval = false,
    }) {
        const fromVault = source === 'vault';
        if (
            !itemId
            || !itemInstanceId
            || !operationId
            || (fromVault
                ? !Number.isInteger(Number(sourceSlotIndex))
                : (!Number.isInteger(Number(x)) || !Number.isInteger(Number(y))))
        ) {
            throw new Error('An equipment source slot and operation ID are required.');
        }
        const response = await apiPost('/game/items/salvage', JSON.stringify({
            operationId,
            itemId,
            itemInstanceId,
            ...(confirmStarterRemoval ? { confirmStarterRemoval: true } : {}),
            ...(fromVault ? { source } : {}),
            ...(fromVault
                ? { sourceSlotIndex: Number(sourceSlotIndex) }
                : { x: Number(x), y: Number(y) }),
        }));
        return response.json();
    }

    static async previewSalvage({
        itemId,
        itemInstanceId,
        x,
        y,
        source = 'pack',
        sourceSlotIndex,
    }) {
        const fromVault = source === 'vault';
        if (
            !itemId
            || !itemInstanceId
            || (fromVault
                ? !Number.isInteger(Number(sourceSlotIndex))
                : (!Number.isInteger(Number(x)) || !Number.isInteger(Number(y))))
        ) {
            throw new Error('An equipment source slot is required.');
        }
        const response = await apiPost('/game/items/salvage/preview', JSON.stringify({
            itemId,
            itemInstanceId,
            ...(fromVault ? { source } : {}),
            ...(fromVault
                ? { sourceSlotIndex: Number(sourceSlotIndex) }
                : { x: Number(x), y: Number(y) }),
        }));
        return response.json();
    }

    static async fetchMarketListings(filters = {}) {
        const response = await apiGet(`/Market/listings${GameAPI.buildQuery(filters)}`);
        return response.json();
    }

    static async createMarketListing({
        operationId,
        itemId,
        itemInstanceId = '',
        sourceX,
        sourceY,
        price,
    }) {
        if (
            !operationId
            || !itemId
            || !Number.isInteger(Number(sourceX))
            || !Number.isInteger(Number(sourceY))
            || !Number.isInteger(Number(price))
        ) {
            throw new Error('A Pack slot, whole-stack price, and operation ID are required.');
        }
        const response = await apiPost('/Market/listings', JSON.stringify({
            operationId,
            itemId,
            itemInstanceId,
            sourceX: Number(sourceX),
            sourceY: Number(sourceY),
            price: Number(price),
        }));
        return response.json();
    }

    static async buyMarketListing(listingId, { operationId } = {}) {
        if (!listingId || !operationId) {
            throw new Error('A market listing and operation ID are required.');
        }
        const response = await apiPost(
            `/Market/listings/${encodeURIComponent(listingId)}/buy`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async cancelMarketListing(listingId, { operationId } = {}) {
        if (!listingId || !operationId) {
            throw new Error('A market listing and operation ID are required.');
        }
        const response = await apiPost(
            `/Market/listings/${encodeURIComponent(listingId)}/cancel`,
            JSON.stringify({ operationId }),
        );
        return response.json();
    }

    static async fetchMarketDeliveries() {
        const response = await apiGet('/Market/deliveries');
        return response.json();
    }

    static async claimMarketDelivery(deliveryId, {
        operationId,
        amount,
    } = {}) {
        if (!deliveryId || !operationId) {
            throw new Error('A market delivery and operation ID are required.');
        }
        const payload = { operationId };
        if (amount !== undefined && amount !== null) {
            const parsedAmount = Number(amount);
            if (!Number.isInteger(parsedAmount) || parsedAmount < 1 || parsedAmount > 20) {
                throw new Error('Scrap proceeds must be claimed in amounts from 1 to 20.');
            }
            payload.amount = parsedAmount;
        }
        const response = await apiPost(
            `/Market/deliveries/${encodeURIComponent(deliveryId)}/claim`,
            JSON.stringify(payload),
        );
        return response.json();
    }

    static async fetchRarity(rarityId) {
        if (!rarityId) return null;
        try {
            const cachedItem = GameAPI.fetchRarityCached(rarityId);
            if (cachedItem) {
                return cachedItem;
            }

            const response = await apiGet(`/rarity/${rarityId}`);
            if (!response.ok) return null;
            const rarity = await response.json();
            localStorage.setItem(contentCacheKey('rarity', rarityId), JSON.stringify(rarity));

            return rarity;
        } catch (error) {
            console.error('Error fetching rarity:', error);
            return null;
        }
    }

    static async fetchRarityCatalog() {
        const response = await apiGet('/rarity');
        if (!response.ok) {
            throw new Error('Crafting rarities could not be loaded.');
        }
        const rarities = await response.json();
        if (!Array.isArray(rarities)) {
            throw new Error('Crafting rarity data is invalid.');
        }
        rarities.forEach((rarity) => {
            const rarityId = rarity?.id ?? rarity?.Id;
            if (rarityId) {
                localStorage.setItem(
                    contentCacheKey('rarity', rarityId),
                    JSON.stringify(rarity),
                );
            }
        });
        return rarities;
    }

    static fetchRarityCached(rarityId) {
        if (!rarityId) return null;
        const cachedRarity = localStorage.getItem(contentCacheKey('rarity', rarityId));
        return cachedRarity ? JSON.parse(cachedRarity) : null;
    }

    static async fetchRarities(rarityIds) {
        const uniqueRarityIds = [...new Set(rarityIds.filter(Boolean))];
        await Promise.all(uniqueRarityIds.map((rarityId) => GameAPI.fetchRarity(rarityId)));
    }

}
